import React, { useEffect, useState, useCallback, useContext } from "react";
import { IoMdAdd, IoIosCloseCircleOutline } from "react-icons/io";
import { MdModeEdit, MdCheck } from "react-icons/md";
import { toast } from "react-toastify";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

import axiosInstance from "../../Context/axiosInstance.js";
import { DarkModeContext } from "../../Context/DarkModeContext.jsx";

const LOCAL_COUNTRIES = [
  { name: "Egypt", currency: "EGP", code: "EG" },
  { name: "Saudi Arabia", currency: "SAR", code: "SA" },
  { name: "United Arab Emirates", currency: "AED", code: "AE" },
  // { name: "Netherlands", currency: "EUR", code: "NL" },
  // { name: "United States", currency: "USD", code: "US" },
  // { name: "United Kingdom", currency: "GBP", code: "GB" },
];

function AdminStores() {
  const { isDarkMode } = useContext(DarkModeContext);
  const countries = LOCAL_COUNTRIES;

  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("Id");
  const [sortDirection, setSortDirection] = useState("asc");

  const navigate = useNavigate();

  // Add modal
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", currency: "", code: "" });
  const [selectedAddCountry, setSelectedAddCountry] = useState("");
  const [addAnimation, setAddAnimation] = useState("");
  const [loadingAdd, setLoadingAdd] = useState(false);

  // Edit modal
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    currency: "",
    code: "",
  });
  const [selectedEditCountry, setSelectedEditCountry] = useState("");
  const [editAnimation, setEditAnimation] = useState("");
  const [editingStore, setEditingStore] = useState(null);

  // Whether the selected country for Add modal is already present as a store
  const isAddCountryAlreadyPresent = Boolean(
    selectedAddCountry &&
    stores.some(
      (s) =>
        String(s.code || s.countryCode || s.code).toUpperCase() ===
        String(selectedAddCountry).toUpperCase(),
    ),
  );

  // Prepare select options
  const countryOptions = countries.map((c) => {
    const alreadyAdded = stores.some(
      (store) =>
        String(store.code || store.countryCode || "").toUpperCase() ===
        String(c.code).toUpperCase(),
    );

    return {
      value: c.code,
      label: c.name,
      currency: c.currency,
      alreadyAdded,
    };
  });

  // Fetch stores
  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/stores?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
          searchQuery,
        )}`,
      );
      setStores(res.data.response);
      setTotalPages(Math.ceil(res.data.totalCount / pageSize));
    } catch (err) {
      toast.error("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortColumn, sortDirection, searchQuery]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const toggleSortDirection = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      navigate(`?page=${newPage}&pageSize=${pageSize}`);
    }
  };

  /** ------------------ ADD ------------------ **/
  // Open Add Modal
  const openAdd = () => {
    setAddForm({ name: "", currency: "", code: "" }); // empty form
    setSelectedAddCountry(""); // no country selected
    setIsAddOpen(true);
    setAddAnimation("slide-in");
  };

  const closeAdd = () => {
    setAddAnimation("slide-out");
    setTimeout(() => {
      setAddForm({ name: "", currency: "", code: "" });
      setSelectedAddCountry("");
      setIsAddOpen(false);
      setAddAnimation("");
    }, 300);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name || !addForm.currency) return toast.error("Required");
    setLoadingAdd(true);
    try {
      await axiosInstance.post("/stores", addForm);
      toast.success("Store added");
      fetchStores();
      closeAdd();
    } catch (err) {
      toast.error("Failed to add store");
    } finally {
      setLoadingAdd(false);
    }
  };

  /** ------------------ EDIT ------------------ **/
  const openEdit = (store) => {
    if (!store) return;
    const country = countries.find((c) => c.currency === store.currency);
    if (country) {
      setEditForm({
        name: country.name,
        currency: country.currency,
        code: country.code,
      });
      setSelectedEditCountry(country.code);
    }
    setEditingStore(store);
    setIsEditOpen(true);
    setEditAnimation("slide-in");
  };

  const closeEdit = () => {
    setEditAnimation("slide-out");
    setTimeout(() => {
      setEditForm({ name: "", currency: "", code: "" });
      setSelectedEditCountry("");
      setEditingStore(null);
      setIsEditOpen(false);
      setEditAnimation("");
    }, 300);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name || !editForm.currency) return toast.error("Required");
    setLoadingEdit(true);
    try {
      await axiosInstance.put(`/stores/${editingStore.id}`, editForm);
      toast.success("Store updated");
      fetchStores();
      closeEdit();
    } catch (err) {
      toast.error("Failed to update store");
    } finally {
      setLoadingEdit(false);
    }
  };

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#292929] text-white" : "bg-white text-black"
      } p-4 lg:p-8 min-h-screen`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } p-4 lg:p-8 mt-[40px] lg:mt-0 rounded-lg shadow-xl flex-1`}
      >
        {/* Actions */}
        <div className="flex flex-col lg:flex-row justify-between mb-4 gap-2">
          <input
            type="text"
            placeholder="Search Stores"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`px-4 py-2 border rounded-lg ${
              isDarkMode ? "bg-[#292929]" : "bg-gray-100"
            }`}
          />
          <div className="flex gap-2">
            <button
              onClick={openAdd}
              className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <IoMdAdd /> Add Store
            </button>
            <button
              disabled={selectedStores.length !== 1}
              onClick={() =>
                openEdit(stores.find((s) => s.id === selectedStores[0]))
              }
              className={`flex items-center gap-2 bg-yellow-500 text-white px-4 py-2 rounded-lg ${
                selectedStores.length !== 1
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-yellow-600"
              }`}
            >
              <MdModeEdit /> Edit Store
            </button>
          </div>
        </div>

        {/* Table */}
        {/* <div className="h-[830px] flex flex-col rounded-lg overflow-hidden"> */}
        <div className="overflow-x-auto h-[650px] min-w-0 max-w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    onChange={(e) =>
                      setSelectedStores(
                        e.target.checked ? stores.map((s) => s.id) : [],
                      )
                    }
                    checked={
                      stores.length > 0 &&
                      selectedStores.length === stores.length
                    }
                  />
                </th>
                <th
                  className="cursor-pointer px-4 py-2 text-center"
                  onClick={() => toggleSortDirection("Id")}
                >
                  Id
                  {sortColumn === "Id" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  className="cursor-pointer px-4 py-2 text-center"
                  onClick={() => toggleSortDirection("Name")}
                >
                  Name
                  {sortColumn === "Name" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  className="cursor-pointer px-4 py-2 text-center"
                  onClick={() => toggleSortDirection("Currency")}
                >
                  Currency
                  {sortColumn === "Currency" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="h-[500px] relative">
                    <div className="absolute inset-0 flex justify-center items-center">
                      <ClipLoader color="#2264E5" size={50} />
                    </div>
                  </td>
                </tr>
              ) : stores.length > 0 ? (
                stores.map((store) => (
                  <tr className="animate-zoomIn" key={store.id}>
                    <td className="text-center px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedStores.includes(store.id)}
                        onChange={() =>
                          setSelectedStores((prev) =>
                            prev.includes(store.id)
                              ? prev.filter((id) => id !== store.id)
                              : [...prev, store.id],
                          )
                        }
                      />
                    </td>
                    <td className="text-center px-4 py-2">{store.id}</td>
                    <td className="text-center px-4 py-2">{store.name}</td>
                    <td className="text-center px-4 py-2">{store.currency}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    No stores found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end py-4 text-black">
          <label
            htmlFor="pageSize"
            className={`${
              isDarkMode ? "text-white" : "text-gray-700"
            } mr-2 text-sm`}
          >
            Stores per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {[20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"
            }`}
          >
            Previous
          </button>
          <span className={"text-lg mt-2 sm:mt-0"}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page >= totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Next
          </button>
        </div>
        {/* </div> */}
      </div>

      {/* Add Modal */}
      {isAddOpen && (
        <Modal title="Add Store" animation={addAnimation} onClose={closeAdd}>
          <div className="w-[310px] space-y-4 max-w-full">
            {/* Country */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Country
              </label>

              <Select
                value={
                  countryOptions.find((c) => c.value === selectedAddCountry) ||
                  null
                }
                onChange={(option) => {
                  if (!option) {
                    setSelectedAddCountry("");
                    setAddForm({ name: "", currency: "", code: "" });
                    return;
                  }

                  setSelectedAddCountry(option.value);
                  setAddForm({
                    name: option.label,
                    currency: option.currency,
                    code: option.value,
                  });
                }}
                options={countryOptions}
                placeholder="Select country"
                isClearable
                isOptionDisabled={(option) => option.alreadyAdded}
                formatOptionLabel={(option) => (
                  <div className="flex w-full items-center justify-between gap-4">
                    <span>{option.label}</span>

                    {option.alreadyAdded && (
                      <span className="flex items-center gap-1 text-xs font-semibold text-green-600">
                        <MdCheck size={15} />
                        Added
                      </span>
                    )}
                  </div>
                )}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: 44,
                    borderRadius: 12,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    cursor: "pointer",
                  }),

                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.data.alreadyAdded
                      ? "#dcfce7"
                      : state.isFocused
                        ? "#f3f4f6"
                        : "#ffffff",
                    color: state.data.alreadyAdded ? "#15803d" : "#111827",
                    fontWeight: state.data.alreadyAdded ? 500 : 400,
                    cursor: state.data.alreadyAdded ? "not-allowed" : "pointer",

                    // react-select normally fades disabled options
                    opacity: 1,
                  }),

                  menu: (provided) => ({
                    ...provided,
                    zIndex: 50,
                  }),
                }}
              />
            </div>

            {/* Currency */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Currency
              </label>

              <input
                type="text"
                value={addForm.currency}
                readOnly
                placeholder="Currency will auto-fill"
                className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none ${
                  isAddCountryAlreadyPresent
                    ? "border-green-300 bg-green-50 text-green-700"
                    : "border-gray-200 bg-gray-50 text-gray-700"
                }`}
              />
            </div>

            {/* Added state */}
            {isAddCountryAlreadyPresent && (
              <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-sm font-medium text-green-700">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white">
                  <MdCheck size={14} />
                </div>
                This store has already been added
              </div>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={isAddCountryAlreadyPresent ? undefined : submitAdd}
              disabled={
                !selectedAddCountry || isAddCountryAlreadyPresent || loadingAdd
              }
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                isAddCountryAlreadyPresent
                  ? "cursor-not-allowed bg-green-500 text-white"
                  : !selectedAddCountry || loadingAdd
                    ? "cursor-not-allowed bg-gray-200 text-gray-500"
                    : "bg-[#2264E5] text-white hover:bg-[#1d56c7]"
              }`}
            >
              {isAddCountryAlreadyPresent ? (
                <>
                  <MdCheck size={18} />
                  Added
                </>
              ) : loadingAdd ? (
                "Saving..."
              ) : (
                "Add Store"
              )}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {isEditOpen && (
        <Modal title="Edit Store" animation={editAnimation} onClose={closeEdit}>
          <Select
            value={
              countryOptions.find((c) => c.value === selectedEditCountry) ||
              null
            }
            onChange={(option) => {
              if (!option) return;
              setSelectedEditCountry(option.value);
              setEditForm({
                name: option.label,
                currency: option.currency,
                code: option.value,
              });
            }}
            options={countryOptions}
            placeholder="Select country"
            styles={{
              container: (provided) => ({
                ...provided,
                width: "100%",
                minWidth: 0,
              }),
              menu: (provided) => ({ ...provided, width: "100%", minWidth: 0 }),
            }}
          />

          <input
            type="text"
            value={editForm.currency}
            readOnly
            placeholder="Currency will auto-fill"
            className="w-full px-3 py-2 rounded-xl border border-black border-opacity-20 mt-2 bg-gray-100 cursor-not-allowed"
          />

          <button
            onClick={submitEdit}
            className="bg-[#2264E5] text-white py-2 px-4 rounded-md mt-2"
          >
            {loadingEdit ? "Saving..." : "Update"}
          </button>
        </Modal>
      )}
    </div>
  );
}

// Simple Modal wrapper
const Modal = ({ children, title, animation, onClose, DarkMode }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 p-4 overflow-y-auto">
    <div
      className={` max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-full ${animation}     ${
        DarkMode ? "bg-gray-300 text-black" : "bg-white text-black"
      } relative flex flex-col bg-white p-6 rounded-[35px] shadow-lg w-[350px]   `}
    >
      <IoIosCloseCircleOutline
        onClick={onClose}
        className="absolute top-4 right-4 text-2xl cursor-pointer text-gray-500 hover:text-red-500"
      />
      <h2 className="text-2xl font-bold text-center mb-2">{title}</h2>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  </div>
);
export default AdminStores;
